import { comCorpo } from '@/lib/pedido';

/**
 * A importação da frota pela planilha (0060) — o lado da tela.
 *
 * A planilha vai inteira ao servidor, em base64: ele lê `.xlsx` e `.csv`, e é
 * ele que confere contra o banco. A tela só escolhe o arquivo, mostra o que
 * cada linha vai virar e pede a confirmação.
 */

export type SituacaoDaLinha = 'entra' | 'ja_cadastrada' | 'erro';

export interface LinhaConferida {
  readonly numeroLinha: number;
  /**
   * 0131 (decisão 57): a planilha não traz mais código. Na linha que ENTRA, é
   * o código que o sistema vai criar para ela (prévia, pelo tipo e pela
   * sequência); na JÁ CADASTRADA, o código da máquina que já existe.
   */
  readonly codigo: string | null;
  readonly descricao: string | null;
  readonly fabricante: string | null;
  readonly modelo: string | null;
  readonly configuracao: string;
  readonly categoria: string | null;
  readonly numeroSerie: string | null;
  readonly placa: string | null;
  readonly tipoMedidor: 'horimetro' | 'odometro';
  readonly situacao: SituacaoDaLinha;
  readonly erros: readonly string[];
}

export interface Conferencia {
  readonly linhas: readonly LinhaConferida[];
  readonly entram: number;
  readonly jaCadastradas: number;
  readonly comErro: number;
  readonly novos: {
    readonly fabricantes: readonly string[];
    readonly modelos: readonly string[];
    readonly configuracoes: readonly string[];
  };
}

export interface ResultadoDaImportacao {
  readonly importadas: number;
  readonly jaCadastradas: number;
  readonly comErro: number;
  readonly criados: { readonly fabricantes: number; readonly modelos: number; readonly configuracoes: number };
}

export interface ArquivoEscolhido {
  readonly nomeArquivo: string;
  readonly conteudo: string;
}

/** O mesmo teto do servidor: 5 MB. */
export const TAMANHO_MAXIMO_DA_PLANILHA = 5 * 1024 * 1024;

export const apiDaImportacao = {
  conferir: (arquivo: ArquivoEscolhido) => comCorpo<Conferencia>('POST', '/equipamentos/importacao/conferencia', arquivo),
  importar: (arquivo: ArquivoEscolhido) => comCorpo<ResultadoDaImportacao>('POST', '/equipamentos/importacao', arquivo),
};

/** Bytes para base64, em pedaços: `btoa` de uma string enorme estoura a pilha. */
export function paraBase64(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  let binario = '';
  for (let i = 0; i < u8.length; i += 0x8000) {
    binario += String.fromCharCode(...u8.subarray(i, i + 0x8000));
  }
  return btoa(binario);
}

export function situacaoEscrita(s: SituacaoDaLinha): string {
  if (s === 'entra') return 'Entra';
  if (s === 'ja_cadastrada') return 'Já cadastrada';
  return 'Com erro';
}

/** Erro primeiro: é o que a pessoa precisa corrigir. Depois o que entra, e o resto. */
export function ordenarParaConferir(linhas: readonly LinhaConferida[]): readonly LinhaConferida[] {
  const peso: Record<SituacaoDaLinha, number> = { erro: 0, entra: 1, ja_cadastrada: 2 };
  return [...linhas].sort((a, b) => peso[a.situacao] - peso[b.situacao] || a.numeroLinha - b.numeroLinha);
}

/**
 * O código que a conferência mostra na linha (0131): na que ENTRA, o que o
 * sistema vai criar para ela; na JÁ CADASTRADA, o da máquina que existe; na
 * com erro, nenhum, porque ela não entra.
 */
export function codigoDaLinha(l: Pick<LinhaConferida, 'codigo' | 'situacao'>): string {
  if (l.situacao === 'erro') return 'Sem código';
  if (l.codigo) return l.codigo;
  return l.situacao === 'entra' ? 'Criado ao importar' : 'Sem código';
}

/** Por que a linha já cadastrada fica de fora, e o que fazer. */
export const TEXTO_DA_JA_CADASTRADA = 'Já está no sistema, com este código ou este número de série. A ficha não muda; ajuste pela ficha.';

/** A frase do formato da planilha, a mesma na mesa e no celular. */
export const TEXTO_DO_FORMATO =
  'Fabricante, modelo e configuração que não existem são criados. A planilha não traz código: cada máquina recebe o seu ao importar, pelo tipo (a coluna Categoria / Tipo, da lista de tipos) e pela sequência. Máquina com número de série já cadastrado fica de fora.';

/**
 * O cabeçalho do modelo para baixar: os nomes que o leitor reconhece, com as
 * marcas da planilha da empresa — `*` obrigatório, `~` quando houver.
 *
 * Sem 'COD Equipamento Sistema' desde a 0131 (decisão 57, pedido do dono em
 * 29/10/2026: "modifique o modelo e tire essa coluna"): o código é do sistema
 * e nasce de '* Categoria / Tipo', que tem de estar na lista de tipos.
 */
export const CABECALHO_DO_MODELO = [
  'COD do patrimônio',
  'Código interno',
  '* Fabricante',
  '* Modelo',
  'Tipo de Combustivel',
  'Configuração / Variante',
  '* Categoria / Tipo',
  'Descrição / Apelido',
  '* Nº de série / Chassi',
  '* Ano de fabricação',
  'Ano do modelo',
  'Ano da aquisição',
  '~ Placa',
  '~ Renavam',
] as const;

/** O modelo em CSV com `;`, que o Excel em português abre direto em colunas. */
export function modeloEmCsv(): string {
  const exemplo = [
    'PAT-1187', 'CB-07', 'CATERPILLAR', '320 GC', 'Diesel', '-', 'Escavadeira hidráulica',
    'Escavadeira 320 amarela', 'CAT0320GKXYZ12345', '2022', '2022', '2024', '-', '-',
  ];
  return `\uFEFF${CABECALHO_DO_MODELO.join(';')}\r\n${exemplo.join(';')}\r\n`;
}
